import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { apiFetch } from '../utils/apiFetch';

const PROFILE_ORDER = [
  'FOOD_PREPARED',
  'PIZZA',
  'BURGER_SNACK',
  'PORTION',
  'DESSERT',
  'NON_ALCOHOLIC_DRINK',
  'WATER',
  'COMBO',
  'DELIVERY_FEE',
  'OTHER',
];

const REQUIRED_PROFILE_FIELDS = ['ncm', 'cfop', 'origin', 'pisCst', 'cofinsCst', 'unit', 'gtin'];

const emptyConfig = {
  enabled: false,
  provider: 'sefaz_direct',
  environment: 'homologation',
  uf: '',
  cnpj: '',
  legalName: '',
  tradeName: '',
  stateRegistration: '',
  municipalRegistration: '',
  municipalityCode: '',
  taxRegime: '',
  cscId: '',
  cscToken: '',
  series: '',
  nextNumber: '',
  notaFiscalSempre: false,
};

const toText = (value) => String(value ?? '').trim();

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('pt-BR');
};

const normalizeSummary = (summary = {}) => ({
  ...summary,
  featureFlags: summary.featureFlags || {},
  config: summary.config || null,
  certificate: summary.certificate || null,
  profiles: Array.isArray(summary.profiles) ? summary.profiles : [],
  health: summary.health || {},
});

const profileToDraft = (profile = {}) => ({
  code: profile.code || '',
  name: profile.name || profile.code || '',
  description: profile.description || '',
  documentType: profile.documentType || 'NFCe',
  uf: profile.uf || '',
  taxRegime: profile.taxRegime || '',
  ncm: profile.ncm || '',
  cfop: profile.cfop || '',
  origin: profile.origin || '',
  csosn: profile.csosn || '',
  cst: profile.cst || '',
  pisCst: profile.pisCst || '',
  cofinsCst: profile.cofinsCst || '',
  cest: profile.cest || '',
  gtin: profile.gtin || '',
  unit: profile.unit || '',
  enabled: profile.enabled !== false,
  isDefault: Boolean(profile.isDefault),
  needsReview: profile.needsReview !== false,
  reviewedAt: profile.reviewedAt || null,
  reviewedBy: profile.reviewedBy || '',
  source: profile.source || 'suggested',
});

const profileMissingFields = (profile = {}) => {
  const missing = REQUIRED_PROFILE_FIELDS.filter((field) => !toText(profile[field]));
  if (!toText(profile.csosn) && !toText(profile.cst)) missing.push('csosn ou cst');
  return missing;
};

const fileNameFromDisposition = (disposition, fallback) => {
  const match = String(disposition || '').match(/filename="?([^"]+)"?/i);
  return match?.[1] || fallback;
};

const FiscalSettingsPanel = ({
  apiUrl,
  initialSummary = null,
  companySettings = {},
  onSummaryChange,
}) => {
  const [summary, setSummary] = useState(() => normalizeSummary(initialSummary || {}));
  const [configDraft, setConfigDraft] = useState(emptyConfig);
  const [profileDrafts, setProfileDrafts] = useState({});
  const [certificateFile, setCertificateFile] = useState(null);
  const [certificatePassword, setCertificatePassword] = useState('');
  const [orderId, setOrderId] = useState('');
  const [preview, setPreview] = useState(null);
  const [issueResult, setIssueResult] = useState(null);
  const [sefazStatus, setSefazStatus] = useState(null);
  const [savingProfileCode, setSavingProfileCode] = useState('');
  const [fiscalLoading, setFiscalLoading] = useState(false);
  const [fiscalSaving, setFiscalSaving] = useState(false);
  const [fiscalError, setFiscalError] = useState('');
  const [fiscalSuccess, setFiscalSuccess] = useState('');

  const featureFlags = summary.featureFlags || {};
  const health = summary.health || {};
  const certificate = summary.certificate || null;
  const config = summary.config || null;
  const fiscalEngineEnabled = featureFlags.fiscalEngineEnabled ?? featureFlags.engineEnabled;
  const sefazDirectEnabled = featureFlags.sefazDirectProviderEnabled ?? featureFlags.sefazDirectEnabled;

  const orderedProfiles = useMemo(() => {
    const byCode = new Map((summary.profiles || []).map((profile) => [profile.code, profile]));
    const ordered = PROFILE_ORDER.map((code) => byCode.get(code)).filter(Boolean);
    const extras = (summary.profiles || []).filter((profile) => !PROFILE_ORDER.includes(profile.code));
    return [...ordered, ...extras];
  }, [summary.profiles]);

  const profilesReviewed = orderedProfiles.length > 0 && orderedProfiles.every((profile) => profile.needsReview === false);

  const setSummaryState = (nextSummary) => {
    const normalized = normalizeSummary(nextSummary);
    setSummary(normalized);
    onSummaryChange?.(normalized);
  };

  const applySummaryToDrafts = (nextSummary) => {
    const normalized = normalizeSummary(nextSummary);
    const nextConfig = normalized.config || {};
    setConfigDraft({
      ...emptyConfig,
      ...nextConfig,
      environment: 'homologation',
      cscToken: '',
      series: nextConfig.series ?? '',
      nextNumber: nextConfig.nextNumber ?? '',
      notaFiscalSempre: Boolean(nextConfig.notaFiscalSempre),
    });
    const drafts = {};
    (normalized.profiles || []).forEach((profile) => {
      drafts[profile.code] = profileToDraft(profile);
    });
    setProfileDrafts(drafts);
  };

  const loadFiscalSettings = async () => {
    setFiscalLoading(true);
    setFiscalError('');
    try {
      const response = await apiFetch(`${apiUrl}/api/settings/fiscal`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao carregar configurações fiscais.');
      setSummaryState(data);
      applySummaryToDrafts(data);
    } catch (err) {
      setFiscalError(err.message || 'Falha ao carregar configurações fiscais.');
    } finally {
      setFiscalLoading(false);
    }
  };

  useEffect(() => {
    if (initialSummary) {
      setSummaryState(initialSummary);
      applySummaryToDrafts(initialSummary);
      return;
    }
    loadFiscalSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfigChange = (key) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setConfigDraft((prev) => ({ ...prev, [key]: value }));
  };

  const saveConfig = async () => {
    setFiscalSaving(true);
    setFiscalError('');
    setFiscalSuccess('');
    try {
      const response = await apiFetch(`${apiUrl}/api/settings/fiscal`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(configDraft),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao salvar configuração fiscal.');
      setSummaryState(data);
      applySummaryToDrafts(data);
      setFiscalSuccess('Configuração fiscal salva.');
    } catch (err) {
      setFiscalError(err.message || 'Falha ao salvar configuração fiscal.');
    } finally {
      setFiscalSaving(false);
    }
  };

  const uploadCertificate = async () => {
    if (!certificateFile) {
      setFiscalError('Selecione um arquivo .pfx antes de enviar.');
      return;
    }
    setFiscalSaving(true);
    setFiscalError('');
    setFiscalSuccess('');
    try {
      const formData = new FormData();
      formData.append('certificate', certificateFile);
      formData.append('password', certificatePassword);
      const response = await apiFetch(`${apiUrl}/api/settings/fiscal/certificate`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao enviar certificado A1.');
      setSummaryState(data);
      applySummaryToDrafts(data);
      setCertificateFile(null);
      setCertificatePassword('');
      setFiscalSuccess('Certificado A1 enviado.');
    } catch (err) {
      setFiscalError(err.message || 'Falha ao enviar certificado A1.');
    } finally {
      setFiscalSaving(false);
    }
  };

  const handleProfileChange = (code, key) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setProfileDrafts((prev) => ({
      ...prev,
      [code]: {
        ...(prev[code] || { code }),
        [key]: key === 'needsReview' ? !value : value,
      },
    }));
  };

  const saveProfile = async (code) => {
    const draft = profileDrafts[code] || {};
    if (draft.needsReview === false) {
      const missing = profileMissingFields(draft);
      if (missing.length) {
        setFiscalError(`Preencha ${missing.join(', ')} antes de marcar ${code} como revisado.`);
        return;
      }
    }
    setSavingProfileCode(code);
    setFiscalError('');
    setFiscalSuccess('');
    try {
      const response = await apiFetch(`${apiUrl}/api/settings/fiscal/profiles/${encodeURIComponent(code)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao salvar perfil fiscal.');
      setSummaryState(data);
      applySummaryToDrafts(data);
      setFiscalSuccess(`Perfil ${code} salvo.`);
    } catch (err) {
      setFiscalError(err.message || 'Falha ao salvar perfil fiscal.');
    } finally {
      setSavingProfileCode('');
    }
  };

  const generatePreview = async () => {
    const id = toText(orderId);
    if (!id) {
      setFiscalError('Informe o orderId para gerar a prévia fiscal.');
      return;
    }
    setFiscalLoading(true);
    setFiscalError('');
    setFiscalSuccess('');
    setIssueResult(null);
    try {
      const response = await apiFetch(`${apiUrl}/api/fiscal/orders/${encodeURIComponent(id)}/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ debug: true }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao gerar prévia fiscal.');
      setPreview(data);
      setFiscalSuccess('Prévia fiscal gerada.');
    } catch (err) {
      setFiscalError(err.message || 'Falha ao gerar prévia fiscal.');
    } finally {
      setFiscalLoading(false);
    }
  };

  const issueHomologation = async () => {
    if (!preview?.orderId) return;
    setFiscalSaving(true);
    setFiscalError('');
    setFiscalSuccess('');
    try {
      const response = await apiFetch(`${apiUrl}/api/fiscal/orders/${encodeURIComponent(preview.orderId)}/issue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: preview.source }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao emitir NFC-e em homologação.');
      setIssueResult(data);
      setFiscalSuccess('Emissão de homologação processada.');
    } catch (err) {
      setFiscalError(err.message || 'Falha ao emitir NFC-e em homologação.');
    } finally {
      setFiscalSaving(false);
    }
  };

  const querySefazStatus = async () => {
    setFiscalLoading(true);
    setFiscalError('');
    try {
      const response = await apiFetch(`${apiUrl}/api/fiscal/sefaz/status`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao consultar status SEFAZ.');
      setSefazStatus(data);
    } catch (err) {
      setFiscalError(err.message || 'Falha ao consultar status SEFAZ.');
    } finally {
      setFiscalLoading(false);
    }
  };

  const downloadArtifact = async (kind) => {
    const invoiceId = issueResult?.invoiceId;
    if (!invoiceId) return;
    setFiscalError('');
    try {
      const response = await apiFetch(`${apiUrl}/api/fiscal/invoices/${invoiceId}/${kind}`);
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Falha ao baixar ${kind.toUpperCase()}.`);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileNameFromDisposition(
        response.headers.get('Content-Disposition'),
        kind === 'xml' ? `nfce-${invoiceId}.xml` : `danfe-${invoiceId}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setFiscalError(err.message || `Falha ao baixar ${kind.toUpperCase()}.`);
    }
  };

  const companyAddress = companySettings.address || {};
  const canIssueHomologation = Boolean(preview?.canIssue && configDraft.environment === 'homologation');

  return (
    <Grid container spacing={3} className="configuracoes-section">
      <Grid size={{ xs: 12 }}>
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          SISTEMA FISCAL EXPERIMENTAL. Visível somente para programador. Não liberar para cliente até homologação completa.
        </Alert>
      </Grid>

      {(fiscalError || fiscalSuccess) && (
        <Grid size={{ xs: 12 }}>
          {fiscalError ? <Alert severity="error">{fiscalError}</Alert> : <Alert severity="success">{fiscalSuccess}</Alert>}
        </Grid>
      )}

      <Grid size={{ xs: 12, lg: 4 }}>
        <Card className="configuracoes-card">
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} mb={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Fiscal Engine</Typography>
                <Typography variant="h6">Status</Typography>
              </Box>
              {fiscalLoading ? <CircularProgress size={22} /> : <Chip label={fiscalEngineEnabled ? 'Ativo' : 'Oculto'} color={fiscalEngineEnabled ? 'success' : 'default'} />}
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={1}>
              <StatusLine label="Engine ativo" ok={fiscalEngineEnabled} />
              <StatusLine label="Provider sefaz_direct" ok={sefazDirectEnabled} />
              <StatusLine label="Ambiente homologação" ok={configDraft.environment === 'homologation'} />
              <StatusLine label="Produção desbloqueada" ok={false} text="Não" />
              <StatusLine label="Certificado presente" ok={health.certificatePresent} />
              <StatusLine label="Certificado vencendo" ok={!health.certificateExpiring} text={health.certificateExpiring ? 'Atenção' : 'Não'} />
              <StatusLine label="IE presente" ok={health.stateRegistrationConfigured} />
              <StatusLine label="CSC presente" ok={health.cscConfigured} />
              <StatusLine label="Série/numeração" ok={health.numberingConfigured} />
              <StatusLine label="Perfis revisados" ok={profilesReviewed} />
            </Stack>
            {health.warnings?.length ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                {health.warnings.join(' ')}
              </Alert>
            ) : null}
            <Button variant="outlined" size="small" sx={{ mt: 2 }} onClick={loadFiscalSettings} disabled={fiscalLoading}>
              Atualizar status
            </Button>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, lg: 8 }}>
        <Card className="configuracoes-card">
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} mb={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Emitente</Typography>
                <Typography variant="h6">Dados fiscais</Typography>
              </Box>
              <Chip label="Somente homologação" color="primary" variant="outlined" />
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}><TextField label="CNPJ" value={configDraft.cnpj || companySettings.cnpj || ''} fullWidth disabled /></Grid>
              <Grid size={{ xs: 12, md: 4 }}><TextField label="Razão social" value={configDraft.legalName || companySettings.legalName || ''} fullWidth disabled /></Grid>
              <Grid size={{ xs: 12, md: 4 }}><TextField label="Nome fantasia" value={configDraft.tradeName || companySettings.tradeName || ''} fullWidth disabled /></Grid>
              <Grid size={{ xs: 12, md: 6 }}><TextField label="Endereço" value={[companyAddress.street, companyAddress.number, companyAddress.neighborhood].filter(Boolean).join(', ')} fullWidth disabled /></Grid>
              <Grid size={{ xs: 12, md: 3 }}><TextField label="Cidade" value={companyAddress.city || ''} fullWidth disabled /></Grid>
              <Grid size={{ xs: 12, md: 3 }}><TextField label="Telefone" value={companySettings.phone || ''} fullWidth disabled /></Grid>
              <Grid size={{ xs: 12, md: 3 }}><TextField label="Inscrição Estadual" value={configDraft.stateRegistration} onChange={handleConfigChange('stateRegistration')} fullWidth /></Grid>
              <Grid size={{ xs: 12, md: 3 }}><TextField label="Regime tributário" value={configDraft.taxRegime} onChange={handleConfigChange('taxRegime')} fullWidth placeholder="Simples Nacional" /></Grid>
              <Grid size={{ xs: 12, md: 2 }}><TextField label="UF" value={configDraft.uf || companyAddress.state || ''} onChange={handleConfigChange('uf')} fullWidth inputProps={{ maxLength: 2 }} /></Grid>
              <Grid size={{ xs: 12, md: 4 }}><TextField label="Código IBGE município" value={configDraft.municipalityCode} onChange={handleConfigChange('municipalityCode')} fullWidth /></Grid>
              <Grid size={{ xs: 12, md: 3 }}><TextField label="CSC ID" value={configDraft.cscId} onChange={handleConfigChange('cscId')} fullWidth /></Grid>
              <Grid size={{ xs: 12, md: 3 }}><TextField label="CSC token" value={configDraft.cscToken} onChange={handleConfigChange('cscToken')} fullWidth type="password" helperText={config?.cscTokenConfigured ? 'Token já salvo; preencha só para trocar.' : ''} /></Grid>
              <Grid size={{ xs: 12, md: 2 }}><TextField label="Série" value={configDraft.series} onChange={handleConfigChange('series')} fullWidth type="number" /></Grid>
              <Grid size={{ xs: 12, md: 2 }}><TextField label="Próximo número" value={configDraft.nextNumber} onChange={handleConfigChange('nextNumber')} fullWidth type="number" /></Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Ambiente</InputLabel>
                  <Select label="Ambiente" value={configDraft.environment} onChange={handleConfigChange('environment')}>
                    <MenuItem value="homologation">Homologação</MenuItem>
                    <MenuItem value="production" disabled>Produção</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Tooltip title="Bloqueado enquanto o Fiscal Engine estiver experimental. Será liberado somente após homologação e rollout.">
                  <span>
                    <FormControlLabel
                      control={<Switch checked={Boolean(configDraft.notaFiscalSempre)} disabled />}
                      label="Nota Fiscal Sempre"
                    />
                  </span>
                </Tooltip>
              </Grid>
            </Grid>
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button variant="contained" onClick={saveConfig} disabled={fiscalSaving}>
                {fiscalSaving ? 'Salvando...' : 'Salvar dados fiscais'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, lg: 5 }}>
        <Card className="configuracoes-card">
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">Certificado A1</Typography>
            <Typography variant="h6" sx={{ mb: 2 }}>Upload seguro</Typography>
            <Stack spacing={2}>
              <Button variant="outlined" component="label">
                Selecionar .pfx
                <input hidden type="file" accept=".pfx,.p12" onChange={(event) => setCertificateFile(event.target.files?.[0] || null)} />
              </Button>
              <Typography variant="body2" color="text.secondary">{certificateFile?.name || 'Nenhum arquivo selecionado.'}</Typography>
              <TextField label="Senha do certificado" value={certificatePassword} onChange={(event) => setCertificatePassword(event.target.value)} type="password" fullWidth />
              <Button variant="contained" onClick={uploadCertificate} disabled={fiscalSaving || !certificateFile}>
                Enviar certificado A1
              </Button>
              {certificate ? (
                <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2 }}>
                  <Typography variant="body2"><strong>Subject:</strong> {certificate.subject || '-'}</Typography>
                  <Typography variant="body2"><strong>Issuer:</strong> {certificate.issuer || '-'}</Typography>
                  <Typography variant="body2"><strong>Thumbprint:</strong> {certificate.thumbprint || '-'}</Typography>
                  <Typography variant="body2"><strong>Validade:</strong> {formatDate(certificate.validFrom)} até {formatDate(certificate.validUntil)}</Typography>
                  {certificate.certificateWarning ? <Alert severity="warning" sx={{ mt: 1 }}>Certificado vencendo em 10 dias ou menos.</Alert> : null}
                </Box>
              ) : <Alert severity="info">Nenhum certificado A1 cadastrado.</Alert>}
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, lg: 7 }}>
        <Card className="configuracoes-card">
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">Teste de pedido</Typography>
            <Typography variant="h6" sx={{ mb: 2 }}>Prévia e homologação</Typography>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                <TextField label="orderId" value={orderId} onChange={(event) => setOrderId(event.target.value)} fullWidth />
                <Button variant="outlined" onClick={generatePreview} disabled={fiscalLoading}>Gerar prévia fiscal</Button>
                {canIssueHomologation ? (
                  <Button variant="contained" color="warning" onClick={issueHomologation} disabled={fiscalSaving}>
                    Emitir NFC-e Homologação
                  </Button>
                ) : null}
              </Stack>
              <Button variant="text" onClick={querySefazStatus} disabled={fiscalLoading}>Consultar status SEFAZ</Button>
              {sefazStatus ? <Alert severity="info">SEFAZ {sefazStatus.uf}: {sefazStatus.sefazStatusCode || '-'} {sefazStatus.sefazReason || ''}</Alert> : null}
              {preview ? (
                <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2 }}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                    <Chip label={`Pedido ${preview.displayId || preview.orderId}`} />
                    <Chip label={preview.canIssue ? 'Pode emitir' : 'Bloqueado'} color={preview.canIssue ? 'success' : 'warning'} />
                    <Chip label={`Itens ${preview.items?.length || 0}`} variant="outlined" />
                  </Stack>
                  {(preview.warnings || []).map((warning) => <Alert key={warning} severity="warning" sx={{ mb: 1 }}>{warning}</Alert>)}
                  {(preview.items || []).map((item, index) => (
                    <Box key={`${item.itemId || item.name}-${index}`} sx={{ py: 1, borderTop: index ? '1px solid #eee' : 'none' }}>
                      <Typography variant="subtitle2">{item.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Perfil: {item.profile?.code || '-'} | Fonte: {item.classificationSource || '-'}
                      </Typography>
                      {item.missingFields?.length ? (
                        <Typography variant="caption" color="error">
                          Faltando: {item.missingFields.join(', ')}
                        </Typography>
                      ) : null}
                    </Box>
                  ))}
                </Box>
              ) : null}
              {issueResult ? (
                <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2 }}>
                  <Typography variant="subtitle2">Resultado SEFAZ</Typography>
                  <Typography variant="body2">Status: {issueResult.status || '-'}</Typography>
                  <Typography variant="body2">Chave: {issueResult.accessKey || '-'}</Typography>
                  <Typography variant="body2">Número: {issueResult.number || '-'} Série: {issueResult.series || '-'}</Typography>
                  <Typography variant="body2">cStat: {issueResult.sefazStatusCode || '-'} {issueResult.sefazReason || ''}</Typography>
                  <Typography variant="body2">Protocolo: {issueResult.protocol || '-'}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    {issueResult.invoiceId ? <Button variant="outlined" onClick={() => downloadArtifact('xml')}>XML</Button> : null}
                    {issueResult.invoiceId ? <Button variant="outlined" onClick={() => downloadArtifact('danfe')}>DANFE</Button> : null}
                  </Stack>
                </Box>
              ) : null}
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Card className="configuracoes-card">
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">Classificação fiscal</Typography>
            <Typography variant="h6" sx={{ mb: 2 }}>Perfis fiscais</Typography>
            <Grid container spacing={2}>
              {orderedProfiles.map((profile) => {
                const draft = profileDrafts[profile.code] || profileToDraft(profile);
                const missing = profileMissingFields(draft);
                return (
                  <Grid key={profile.code} size={{ xs: 12, lg: 6 }}>
                    <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2 }}>
                      <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center" mb={1.5}>
                        <Box>
                          <Typography variant="subtitle2">{profile.code}</Typography>
                          <Typography variant="body2" color="text.secondary">{profile.name}</Typography>
                        </Box>
                        <Chip label={draft.needsReview ? 'Pendente' : 'Revisado'} color={draft.needsReview ? 'warning' : 'success'} size="small" />
                      </Stack>
                      <Grid container spacing={1.2}>
                        {['ncm', 'cfop', 'origin', 'csosn', 'cst', 'pisCst', 'cofinsCst', 'cest', 'gtin', 'unit'].map((field) => (
                          <Grid key={field} size={{ xs: 6, md: 4 }}>
                            <TextField
                              label={field}
                              value={draft[field] || ''}
                              onChange={handleProfileChange(profile.code, field)}
                              fullWidth
                              size="small"
                            />
                          </Grid>
                        ))}
                        <Grid size={{ xs: 12 }}>
                          <FormControlLabel
                            control={<Switch checked={!draft.needsReview} onChange={handleProfileChange(profile.code, 'needsReview')} />}
                            label="Revisado"
                          />
                          {!draft.needsReview && missing.length ? (
                            <Typography variant="caption" color="error" display="block">
                              Para revisar, preencha: {missing.join(', ')}
                            </Typography>
                          ) : null}
                        </Grid>
                      </Grid>
                      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
                        <Button variant="outlined" size="small" onClick={() => saveProfile(profile.code)} disabled={savingProfileCode === profile.code}>
                          {savingProfileCode === profile.code ? 'Salvando...' : 'Salvar perfil'}
                        </Button>
                      </Stack>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

const StatusLine = ({ label, ok, text }) => (
  <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="center">
    <Typography variant="body2">{label}</Typography>
    <Chip
      size="small"
      label={text || (ok ? 'Sim' : 'Não')}
      color={ok ? 'success' : 'default'}
      variant={ok ? 'filled' : 'outlined'}
    />
  </Stack>
);

export default FiscalSettingsPanel;
